import React, {useRef, useCallback, useState, useEffect} from 'react';
import {Box, Stack, styled} from '@mui/material';
import {SwitchButton} from '@lightricks/react-design-system';
import StyledColorPicker from '@contentlab/components/styled-color-picker';
import VideoCaptionOverlayStandalone from '@contentlab/components/video-display/captions-rendering/VideoCaptionOverlayStandalone';
import {
  CaptionsStylePreset,
  Animation,
  AnimationKind,
  Caption,
  StyleConfig,
  FontVariants,
  Alignment,
  TextCapitalization,
  Easing,
  BackgroundAnimationKind,
} from '@contentlab/types/captions';
import translate from '@/utils/translate';
import {
  ANIMATION_OPTIONS,
  Body,
  Icon,
  designSystemToken,
  EditableHexTextField,
} from './SharedConstants';

const AnimationCard = styled('button')<{selected?: boolean}>(({selected}) => ({
  'flex': 1,
  'height': '124px',
  'backgroundColor': designSystemToken('semantic.bg.primary'),
  'borderRadius': '16px',
  'cursor': 'pointer',
  'display': 'flex',
  'flexDirection': 'column',
  'alignItems': 'center',
  'justifyContent': 'center',
  'gap': '8px',
  'padding': '0px',
  'position': 'relative',
  'overflow': 'hidden',
  '&:hover': {
    backgroundColor: designSystemToken('semantic.bg.primary'),
    opacity: 0.8,
  },
  'border': selected
    ? `1px solid ${designSystemToken('semantic.bg.neutral')}`
    : `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
}));

const PreviewContainer = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#F2F2F2',
  borderRadius: '16px',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const DEFAULT_HIGHLIGHT_COLOR = '#FFFF00';
const DEFAULT_WORD_BACKGROUND_COLOR = '#FFFFFF';
const DEFAULT_HIGHLIGHT_COLOR_OPACITY = 1;
const DEFAULT_WORD_BACKGROUND_COLOR_OPACITY = 1;
const DEFAULT_WORD_BACKGROUND_CORNER_RADIUS = 8;
const DEFAULT_WORD_BACKGROUND_VERTICAL_PADDING = 0.4;
const DEFAULT_WORD_BACKGROUND_HORIZONTAL_PADDING = 0.3;
const DEFAULT_TEXT_COLOR = '#2C2D30';
const DEFAULT_ANIMATION_HIGHLIGHT_COLOR = '#FFFFFF';

function createMockCaptions(animationName: string): Caption[] {
  const duration = 3;
  return [
    {
      id: 'animation-preview-caption',
      text: animationName,
      start: 0,
      end: duration,
    },
  ];
}

function useLoopingTime(duration = 3000) {
  const startTimeRef = useRef<number>(Date.now());

  const getCurrentTime = useCallback(() => {
    const now = Date.now();
    const elapsed = now - startTimeRef.current;

    // Reset the start time when we complete a cycle to prevent overflow
    if (elapsed >= duration) {
      startTimeRef.current = now - (elapsed % duration);
    }

    const currentElapsed = now - startTimeRef.current;
    return currentElapsed / 1000;
  }, [duration]);

  return getCurrentTime;
}

function createMockStyleWithAnimation(
  animationType: AnimationKind | null
): StyleConfig {
  const baseStyle: StyleConfig = {
    typography: {
      fontSettings: {
        selectedFont: {
          fontId: 'Brown LL',
          variant: FontVariants.BOLD,
        },
        size: 70,
      },
      textColor: {hex: DEFAULT_TEXT_COLOR, opacity: 1},
      textAlignment: Alignment.CENTER,
      textCapitalization: TextCapitalization.REGULAR,
      captionLines: 2,
      spacing: {
        lineHeight: 1.4,
        kerning: 0,
      },
    },
    styling: {
      textStroke: null,
      dropShadow: null,
      background: null,
    },
    normalizedVerticalPosition: 0.5,
    animation: null,
  };

  if (animationType) {
    baseStyle.animation = {
      type: animationType,
      duration: 3,
      wordSelectionStrategy: null,
      wordColor:
        animationType === AnimationKind.KARAOKE_HIGHLIGHT
          ? {
              hex: `#A256FF`,
              opacity: DEFAULT_HIGHLIGHT_COLOR_OPACITY,
            }
          : null,
      wordMaxScaling:
        animationType === AnimationKind.POP_IN ||
        animationType === AnimationKind.FADE_IN
          ? 1.3
          : null,
      background:
        animationType === AnimationKind.KARAOKE_HIGHLIGHT
          ? {
              color: {
                hex: DEFAULT_ANIMATION_HIGHLIGHT_COLOR,
                opacity: 0.9,
              },
              cornerRadius: 12,
              verticalPadding: DEFAULT_WORD_BACKGROUND_VERTICAL_PADDING,
              horizontalPadding: DEFAULT_WORD_BACKGROUND_HORIZONTAL_PADDING,
            }
          : null,
      backgroundAnimationType:
        animationType === AnimationKind.KARAOKE_HIGHLIGHT
          ? BackgroundAnimationKind.SMOOTH
          : null,
      easing: Easing.EASE_OUT,
    };
  }

  return baseStyle;
}

// Animation preview component
interface AnimationPreviewCardProps {
  animationType: AnimationKind | null;
  animationLabel: string;
  showAnimation?: boolean;
}

function AnimationPreviewCard({
  animationType,
  animationLabel,
  showAnimation = false,
}: AnimationPreviewCardProps) {
  const getCurrentTime = useLoopingTime(3000); // 3 second loop
  const mockCaptions = createMockCaptions(animationLabel);
  const mockStyle = createMockStyleWithAnimation(animationType);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({width: 400, height: 280});

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  const styleWithConditionalAnimation = {
    ...mockStyle,
    animation: showAnimation ? mockStyle.animation : null,
  };

  return (
    <PreviewContainer ref={containerRef}>
      <VideoCaptionOverlayStandalone
        testID={`animation-preview-${animationType || 'none'}`}
        captionsData={{
          captions: mockCaptions,
          languageCode: 'en',
        }}
        style={styleWithConditionalAnimation}
        getCurrentTime={getCurrentTime}
        animationRequired={showAnimation}
        canvasWidth={dimensions.width}
        canvasHeight={dimensions.height}
      />
    </PreviewContainer>
  );
}

const AnimationParamsContainer = styled(Box)(() => ({
  backgroundColor: designSystemToken('semantic.bg.secondary'),
  border: `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
  borderRadius: '16px',
  padding: '16px',
  position: 'relative',
}));

const ColorPickerWithToggleRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0',
  height: '56px',
}));

const ColorPickerLabel = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
}));

const ColorPickerValue = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
}));

const ColorValueDisplay = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

interface ColorValueProps {
  hex: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function ColorValue({hex, onChange}: ColorValueProps) {
  return (
    <ColorValueDisplay>
      <EditableHexTextField value={hex} onChange={onChange} />
      <StyledColorPicker value={hex} onChange={onChange} size="small" />
    </ColorValueDisplay>
  );
}

interface AnimationConfigurationProps {
  editedStyle: CaptionsStylePreset;
  updateAnimation: (animation: Animation | null) => void;
}

// Connected animation configuration component
function AnimationConfiguration({
  editedStyle,
  updateAnimation,
}: AnimationConfigurationProps) {
  const {animation} = editedStyle.captionsStyle;

  const handleHighlightColorChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!animation) return;
    updateAnimation({
      ...animation,
      wordColor: {
        hex: e.target.value,
        opacity:
          animation.wordColor?.opacity || DEFAULT_HIGHLIGHT_COLOR_OPACITY,
      },
    });
  };

  const handleWordBackgroundColorChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!animation) return;
    updateAnimation({
      ...animation,
      background: {
        ...(animation.background || {}),
        color: {
          hex: e.target.value,
          opacity:
            animation.background?.color?.opacity ||
            DEFAULT_WORD_BACKGROUND_COLOR_OPACITY,
        },
        cornerRadius:
          animation.background?.cornerRadius ??
          DEFAULT_WORD_BACKGROUND_CORNER_RADIUS,
        verticalPadding:
          animation.background?.verticalPadding ??
          DEFAULT_WORD_BACKGROUND_VERTICAL_PADDING,
        horizontalPadding:
          animation.background?.horizontalPadding ??
          DEFAULT_WORD_BACKGROUND_HORIZONTAL_PADDING,
      },
    });
  };

  const handleWordColorToggle = () => {
    if (!animation) return;
    updateAnimation({
      ...animation,
      wordColor: animation.wordColor
        ? null
        : {
            hex: DEFAULT_HIGHLIGHT_COLOR,
            opacity: DEFAULT_HIGHLIGHT_COLOR_OPACITY,
          },
    });
  };

  const handleBackgroundToggle = () => {
    if (!animation) return;
    updateAnimation({
      ...animation,
      background: animation.background
        ? null
        : {
            color: {
              hex: DEFAULT_WORD_BACKGROUND_COLOR,
              opacity: DEFAULT_WORD_BACKGROUND_COLOR_OPACITY,
            },
            cornerRadius: DEFAULT_WORD_BACKGROUND_CORNER_RADIUS,
            verticalPadding: DEFAULT_WORD_BACKGROUND_VERTICAL_PADDING,
            horizontalPadding: DEFAULT_WORD_BACKGROUND_HORIZONTAL_PADDING,
          },
    });
  };

  if (animation?.type !== AnimationKind.KARAOKE_HIGHLIGHT) {
    return null;
  }

  return (
    <AnimationParamsContainer>
      <Stack gap="0px">
        <Body size="md" sx={{fontWeight: 500, marginBottom: '8px'}}>
          {translate('Active word')}
        </Body>

        <ColorPickerWithToggleRow>
          <ColorPickerLabel>
            <Icon name="Features-Text" size="medium" appearance="neutral" />
            <Body size="md">{translate('Highlight')}</Body>
          </ColorPickerLabel>

          <ColorPickerValue>
            {animation?.wordColor && (
              <ColorValue
                hex={animation.wordColor.hex}
                onChange={handleHighlightColorChange}
              />
            )}
            <SwitchButton
              checked={!!animation?.wordColor}
              onChange={handleWordColorToggle}
              checkedTrackColor={designSystemToken('semantic.bg.inverse')}
            />
          </ColorPickerValue>
        </ColorPickerWithToggleRow>

        <ColorPickerWithToggleRow>
          <ColorPickerLabel>
            <Icon
              name="Features-TextBackground"
              size="medium"
              appearance="neutral"
            />
            <Body size="md">{translate('Background')}</Body>
          </ColorPickerLabel>

          <ColorPickerValue>
            {animation?.background && (
              <ColorValue
                hex={
                  animation.background.color?.hex ||
                  DEFAULT_WORD_BACKGROUND_COLOR
                }
                onChange={handleWordBackgroundColorChange}
              />
            )}
            <SwitchButton
              checked={!!animation?.background}
              onChange={handleBackgroundToggle}
              checkedTrackColor={designSystemToken('semantic.bg.inverse')}
            />
          </ColorPickerValue>
        </ColorPickerWithToggleRow>
      </Stack>
    </AnimationParamsContainer>
  );
}

interface AnimationSectionProps {
  updateAnimation: (animation: Animation | null) => void;
  editedStyle: CaptionsStylePreset;
}

function AnimationSection({
  updateAnimation,
  editedStyle,
}: AnimationSectionProps) {
  const [hoveredAnimationType, setHoveredAnimationType] =
    useState<AnimationKind | null>(null);

  const handleAnimationSelect = (animationType: AnimationKind | null) => {
    if (animationType === null) {
      updateAnimation(null);
    } else {
      updateAnimation({
        type: animationType,
        duration: null,
        wordSelectionStrategy: null,
        wordColor:
          animationType === AnimationKind.KARAOKE_HIGHLIGHT
            ? {
                hex: DEFAULT_HIGHLIGHT_COLOR,
                opacity: DEFAULT_HIGHLIGHT_COLOR_OPACITY,
              }
            : null,
        wordMaxScaling: null,
        background: null,
        backgroundAnimationType: null,
        easing: null,
      });
    }
  };

  return (
    <Stack gap="24px">
      <Box>
        <Stack gap="16px">
          {Array.from(
            {length: Math.ceil(ANIMATION_OPTIONS.length / 2)},
            (_, rowIndex) => (
              <Stack key={rowIndex} direction="row" gap="16px">
                {ANIMATION_OPTIONS.slice(rowIndex * 2, rowIndex * 2 + 2).map(
                  (animation) => {
                    const isHovered = hoveredAnimationType === animation.value;
                    return (
                      <AnimationCard
                        key={animation.value?.toString() || 'none'}
                        selected={
                          (editedStyle.captionsStyle.animation?.type ||
                            null) === animation.value
                        }
                        onClick={() => handleAnimationSelect(animation.value)}
                        onMouseEnter={() =>
                          setHoveredAnimationType(animation.value)
                        }
                        onMouseLeave={() => setHoveredAnimationType(null)}
                      >
                        <AnimationPreviewCard
                          animationType={animation.value}
                          animationLabel={translate(animation.label)}
                          showAnimation={isHovered}
                        />
                      </AnimationCard>
                    );
                  }
                )}
              </Stack>
            )
          )}
        </Stack>
      </Box>

      {editedStyle.captionsStyle.animation && (
        <AnimationConfiguration
          editedStyle={editedStyle}
          updateAnimation={updateAnimation}
        />
      )}
    </Stack>
  );
}

export default AnimationSection;
